import api from './api';

/*
 * Iconos de sitios por el proxy de nuestro servidor (GET /api/icons/:domain), nunca directo a Google.
 * Plan: design/SETTINGS-DEVELOPER-PRIVACY.md §2.1.
 *
 * La peticion lleva la sesion en la cabecera Authorization (axios) y el icono se muestra como data URL:
 * un <img src> no puede mandar cabeceras y el token en la URL acabaria en logs e historial, asi que
 * no se acepta por query. Data URL y no blob: porque la CSP por defecto de helmet (`img-src 'self' data:`)
 * bloquea las imagenes blob: cuando el panel lo sirve el propio Node. La cache es por host y por pestaña:
 * icono (data URL) o "no hay" (null).
 * Un 204 (ajuste apagado), un 429 o un fallo de red no se cachean: se reintentan la proxima vez.
 */

const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
// TLD que no resuelven en Internet publico (el servidor tambien los rechaza con 400; aqui ni se pide).
const NO_PUBLIC_TLD = /\.(localhost|local|internal|lan|home|corp|intranet|test|example|invalid)$/;
const MAX_CONCURRENT = 6;

const cache = new Map(); // host -> Promise<string|null>
const settled = new Map(); // host -> string|null (para leer sin esperar)
const waiting = [];
let active = 0;

/** Hostname de una URL o dominio sin www; null si no puede tener icono publico (IP, local, sin punto). */
export function iconHost(urlOrHost) {
  if (!urlOrHost || typeof urlOrHost !== 'string') return null;
  const raw = urlOrHost.trim();
  let host;
  try {
    host = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`).hostname;
  } catch {
    return null;
  }
  host = host.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
  if (!host || !host.includes('.') || host.includes(':') || IPV4_RE.test(host)) return null;
  if (host === 'localhost' || NO_PUBLIC_TLD.test(host)) return null;
  return host;
}

function acquire() {
  if (active < MAX_CONCURRENT) { active += 1; return Promise.resolve(); }
  return new Promise((resolve) => waiting.push(resolve));
}
function release() {
  const next = waiting.shift();
  if (next) next(); else active -= 1;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function fetchIcon(host) {
  await acquire();
  try {
    const res = await api.get(`/icons/${encodeURIComponent(host)}`, {
      responseType: 'blob',
      validateStatus: (s) => s === 200 || s === 204 || s === 404,
    });
    if (res.status === 200 && res.data && res.data.size > 0) return { url: await blobToDataUrl(res.data), keep: true };
    return { url: null, keep: res.status === 404 };
  } catch {
    return { url: null, keep: false };
  } finally {
    release();
  }
}

/** Icono ya descargado de `host`: data URL, null (no hay) o undefined (aun no se ha pedido). */
export function peekSiteIcon(host) {
  return settled.get(host);
}

/** Descarga (una vez) el icono de `host`. Resuelve a un data URL o a null. */
export function loadSiteIcon(host) {
  if (!host) return Promise.resolve(null);
  if (cache.has(host)) return cache.get(host);
  const job = fetchIcon(host).then(({ url, keep }) => {
    if (keep) settled.set(host, url);
    else cache.delete(host);
    return url;
  });
  cache.set(host, job);
  return job;
}

/** Olvida todo (al apagar el ajuste o cambiar de cuenta). */
export function clearSiteIcons() {
  settled.clear();
  cache.clear();
}
